"use client";

/**
 * AuthFlow — flow Doctolib-style sur 1 écran sans scroll.
 *
 * 4 étapes :
 *   1. CHOICE     — Carte 'Nouveau ?' + 'Déjà un compte ?' (entry point)
 *   2. EMAIL      — saisie email/tel pour login
 *   3. PASSWORD   — 'Bienvenue [email]' + mot de passe (+ remember + forgot)
 *   4. SIGNUP     — inscription (formulaire complet)
 *
 * Le composant gère son propre stepper interne. Tout reste centré dans
 * une carte, h-screen, pas de scroll.
 */
import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";
import { Calendar, ChatCircle, Heart, ArrowLeft, Eye, EyeSlash } from "@phosphor-icons/react/dist/ssr";
import { safeReturnTo } from "@/lib/auth/safe-redirect";

type Step = "choice" | "email" | "password" | "signup" | "totp";

type Props = {
  mode: "login" | "signup";
  /** Si true, masque le bouton "Créer un compte" (contexte pro : compte créé
   *  manuellement par l'admin après vérification INPE/Ordre). */
  hideSignup?: boolean;
  /** Liste blanche des rôles autorisés (séparation espaces).
   *  - sahha.ma patient : ["PATIENT","PHYSICIAN"] (médecin peut prendre RDV)
   *  - pro/app contexte  : ["PHYSICIAN"] uniquement.
   *  Si fourni, /api/auth/login refuse 403 immédiat avant 2FA si mismatch. */
  allowedRoles?: Array<"PATIENT" | "PHYSICIAN" | "ADMIN">;
};

// Redirige vers l'espace correspondant au rôle. La séparation entre
// /mon-compte (patient), /espace-pro/dashboard (praticien) et /admin (admin)
// est stricte — un médecin connecté via /connexion patient ne doit pas
// atterrir sur /mon-compte. `returnTo` explicite (query string) override.
const ROLE_HOME: Record<string, string> = {
  patient: "/mon-compte",
  pro: "/espace-pro/dashboard",
  admin: "/admin",
};

export function AuthFlowClient({ mode, hideSignup = false, allowedRoles }: Props) {
  // Connexion réservée aux praticiens → leur propre parcours de
  // réinitialisation, qui les ramène dans l'espace Pro et non côté patient.
  const forgotHref =
    allowedRoles?.includes("PHYSICIAN") && !allowedRoles.includes("PATIENT")
      ? "/espace-pro/mot-de-passe-oublie"
      : "/mot-de-passe-oublie";
  const router = useRouter();
  const searchParams = useSearchParams();
  // Redirection ouverte (audit 2026-09-10) — cf. src/lib/auth/safe-redirect.ts.
  // `next` : paramètre posé par le bouton favori (/inscription?next=/medecin/…),
  // la porte de téléconsultation et les pages /mon-compte/* — ignoré jusqu'au
  // 2026-10-21 : après inscription ou connexion, retour à /mon-compte au lieu
  // de la fiche ou de la page demandée.
  const explicitReturnTo = safeReturnTo(searchParams.get("returnTo") ?? searchParams.get("next"), "");
  // i18n auth (audit site 2026-05-28) : AuthFlowClient était 100 % FR sur
  // /ar/connexion, /ar/inscription, /en/connexion, /en/inscription —
  // bloqueur de conversion pour MRE arabophones/anglophones.
  const t = useTranslations("auth");

  function redirectAfterAuth(role: string | undefined) {
    const target = explicitReturnTo || ROLE_HOME[role ?? ""] || "/mon-compte";
    router.push(target);
    router.refresh();
  }

  const [step, setStep] = useState<Step>(mode === "signup" ? "signup" : "choice");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 2FA
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  // SIGNUP fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  function back() {
    setError("");
    if (step === "email") setStep("choice");
    else if (step === "password") setStep("email");
    else if (step === "signup") setStep("choice");
    else if (step === "totp") setStep("password");
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!identifier.trim()) return;
    // On passe directement à l'étape password (pas de check email exists côté
    // public — politique no-enumerate). L'erreur identifiants apparaît à l'étape suivante.
    setStep("password");
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: identifier.trim().toLowerCase(),
          password,
          ...(allowedRoles ? { allowedRoles } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Email ou mot de passe incorrect.");
        setLoading(false);
        return;
      }
      if (data.mfaRequired) {
        setChallengeToken(data.challengeToken);
        setStep("totp");
        setLoading(false);
        return;
      }
      redirectAfterAuth(data?.user?.role);
    } catch {
      setError(t("flow.errors.serverUnreachable"));
      setLoading(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: totpCode, challengeToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("flow.errors.twoFactorInvalid"));
        setLoading(false);
        return;
      }
      redirectAfterAuth(data?.user?.role);
    } catch {
      setError(t("flow.errors.serverUnreachable"));
      setLoading(false);
    }
  }

  async function submitSignup(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("flow.errors.passwordTooShort")); return; }
    if (!acceptTerms) { setError(t("flow.errors.acceptTerms")); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: identifier.trim().toLowerCase(), password, firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim(), role: "patient" }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("flow.errors.signupGeneric"));
        setLoading(false);
        return;
      }
      redirectAfterAuth(data?.user?.role);
    } catch {
      setError(t("flow.errors.serverUnreachable"));
      setLoading(false);
    }
  }

  // ─────────── CHOICE ───────────
  if (step === "choice") {
    return (
      <div>
        <div className="mb-8">
          <h1 className="font-display text-[28px] font-semibold leading-tight tracking-tight text-[var(--sahha-text-900)]">{t("flow.welcome")}</h1>
          <p className="mt-1.5 text-[14px] text-[var(--sahha-text-600)]">
            {hideSignup
              ? t("flow.subtitlePro")
              : t("flow.subtitleDefault")}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setStep("email")}
          className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-600)] text-[14px] font-semibold text-white transition hover:bg-[var(--sahha-primary-700)]"
        >
          {t("login.submit")}
        </button>

        {!hideSignup && (
          <>
            <div className="my-5 flex items-center gap-3 text-[12px] text-[var(--sahha-text-400)]">
              <span className="h-px flex-1 bg-[var(--sahha-border-subtle)]" />
              <span>{t("flow.or")}</span>
              <span className="h-px flex-1 bg-[var(--sahha-border-subtle)]" />
            </div>

            <button
              type="button"
              onClick={() => setStep("signup")}
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[var(--sahha-border-default)] bg-white text-[14px] font-semibold text-[var(--sahha-text-900)] transition hover:border-[var(--sahha-primary-400)] hover:bg-[var(--sahha-surface-1)]"
            >
              {t("signup.title")}
            </button>
          </>
        )}

        <div className="mt-8 rounded-xl border border-[var(--sahha-border-subtle)] bg-[var(--sahha-surface-1)] p-4">
          <ul className="space-y-2.5">
            <Bullet icon={<Calendar size={14} weight="duotone" />} label={t("flow.bullets.booking")} />
            <Bullet icon={<ChatCircle size={14} weight="duotone" />} label={t("flow.bullets.messaging")} />
            <Bullet icon={<Heart size={14} weight="duotone" />} label={t("flow.bullets.records")} />
          </ul>
        </div>
      </div>
    );
  }

  // ─────────── EMAIL ───────────
  if (step === "email") {
    return (
      <div>
        <BackButton onClick={back} label={t("loginPage.backHomeAria")} />
        <div className="mt-6 mb-7">
          <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-[var(--sahha-text-900)]">{t("login.submit")}</h1>
          <p className="mt-1.5 text-[14px] text-[var(--sahha-text-600)]">{t("flow.emailSubtitle")}</p>
        </div>
        <form onSubmit={submitEmail} className="space-y-4">
          <div>
            <label htmlFor="identifier" className="block text-[12.5px] font-semibold text-[var(--sahha-text-700)]">{t("flow.emailLabel")}</label>
            <input
              id="identifier"
              type="text"
              required
              autoFocus
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="nom@email.com"
              className="mt-1.5 h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-[14px] placeholder:text-[var(--sahha-text-400)] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
            />
          </div>
          {error && <Err msg={error} />}
          <button
            type="submit"
            disabled={!identifier.trim()}
            className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-600)] text-[14px] font-semibold text-white hover:bg-[var(--sahha-primary-700)] disabled:opacity-60"
          >
            {t("flow.continue")}
          </button>
        </form>
      </div>
    );
  }

  // ─────────── PASSWORD ───────────
  if (step === "password") {
    return (
      <div>
        <BackButton onClick={back} label={t("loginPage.backHomeAria")} />
        <div className="mt-6 mb-7">
          <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-[var(--sahha-text-900)]">{t("flow.welcomeBack")}</h1>
          <p className="mt-1.5 text-[14px] text-[var(--sahha-text-600)] truncate">{identifier}</p>
        </div>
        <form onSubmit={submitPassword} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-[12.5px] font-semibold text-[var(--sahha-text-700)]">{t("login.password")}</label>
            <div className="relative mt-1.5">
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                required
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 pr-11 text-[14px] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)] hover:text-[var(--sahha-text-900)]"
                aria-label={showPwd ? t("login.hidePassword") : t("login.showPassword")}
              >
                {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-[12.5px] text-[var(--sahha-text-600)]">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded accent-[var(--sahha-primary-600)]"
              />
              {t("login.rememberMe")}
            </label>
            <Link href={forgotHref} className="text-[12.5px] font-semibold text-[var(--sahha-primary-700)] hover:underline">
              {t("login.forgotPassword")}
            </Link>
          </div>
          {error && <Err msg={error} />}
          <button
            type="submit"
            disabled={loading || !password}
            className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-600)] text-[14px] font-semibold text-white hover:bg-[var(--sahha-primary-700)] disabled:opacity-60"
          >
            {loading ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </div>
    );
  }

  // ─────────── 2FA ───────────
  if (step === "totp") {
    return (
      <div>
        <BackButton onClick={back} label={t("loginPage.backHomeAria")} />
        <div className="mt-6 mb-7">
          <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-[var(--sahha-text-900)]">{t("flow.verifyTitle")}</h1>
          <p className="mt-1.5 text-[14px] text-[var(--sahha-text-600)]">{t("flow.totpSubtitle")}</p>
        </div>
        <form onSubmit={submitTotp} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoFocus
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            className="h-14 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-center font-mono text-[20px] tracking-[0.5em] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
          />
          {error && <Err msg={error} />}
          <button
            type="submit"
            disabled={loading || totpCode.length !== 6}
            className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-600)] text-[14px] font-semibold text-white disabled:opacity-60"
          >
            {loading ? t("flow.verifying") : t("flow.verify")}
          </button>
        </form>
      </div>
    );
  }

  // ─────────── SIGNUP ───────────
  return (
    <div>
      {mode !== "signup" && <BackButton onClick={back} label={t("loginPage.backHomeAria")} />}
      <div className={`${mode === "signup" ? "" : "mt-6"} mb-7`}>
        <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-[var(--sahha-text-900)]">{t("signup.title")}</h1>
        <p className="mt-1.5 text-[14px] text-[var(--sahha-text-600)]">{t("flow.signupSubtitle")}</p>
      </div>
      <form onSubmit={submitSignup} className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("signup.firstName")} value={firstName} onChange={setFirstName} required autoComplete="given-name" />
          <Field label={t("signup.lastName")} value={lastName} onChange={setLastName} required autoComplete="family-name" />
        </div>
        <Field label={t("signup.email")} type="email" value={identifier} onChange={setIdentifier} required autoComplete="email" placeholder="nom@email.com" />
        <Field label={t("signup.phone")} type="tel" value={phone} onChange={setPhone} required placeholder="+212 6XX XX XX XX" autoComplete="tel" />
        <div>
          <label className="block text-[12.5px] font-semibold text-[var(--sahha-text-700)]">{t("signup.password")}</label>
          <div className="relative mt-1.5">
            <input
              type={showPwd ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("flow.passwordHint")}
              className="h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 pr-11 text-[14px] placeholder:text-[var(--sahha-text-400)] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
            />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)]" aria-label={t("login.showPassword")}>
              {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <label className="flex items-start gap-2.5 pt-1 text-[12.5px] text-[var(--sahha-text-600)]">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[var(--sahha-primary-600)]" />
          <span>{t("flow.termsAcceptLabel")} <Link href="/cgu" className="font-semibold text-[var(--sahha-primary-700)] underline">{t("flow.termsCgu")}</Link> {t("flow.termsAcceptAnd")} <Link href="/politique-confidentialite" className="font-semibold text-[var(--sahha-primary-700)] underline">{t("flow.termsPrivacy")}</Link>.</span>
        </label>

        {error && <Err msg={error} />}
        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-600)] text-[14px] font-semibold text-white hover:bg-[var(--sahha-primary-700)] disabled:opacity-60"
        >
          {loading ? t("flow.signingUp") : t("signup.submit")}
        </button>

        {mode === "signup" && (
          <p className="pt-2 text-center text-[13px] text-[var(--sahha-text-600)]">
            {t("signup.alreadyAccount")}{" "}
            <button type="button" onClick={() => { setStep("email"); }} className="font-semibold text-[var(--sahha-primary-700)] hover:underline">
              {t("signup.loginLink")}
            </button>
          </p>
        )}
      </form>
    </div>
  );
}

function Err({ msg }: { msg: string }) {
  return <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{msg}</p>;
}

function Bullet({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="flex items-center gap-2.5 text-[13px] text-[var(--sahha-text-700)]">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[var(--sahha-primary-700)] ring-1 ring-[var(--sahha-border-subtle)]">{icon}</span>
      <span>{label}</span>
    </li>
  );
}

function BackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-ml-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12.5px] font-medium text-[var(--sahha-text-500)] hover:bg-[var(--sahha-surface-1)] hover:text-[var(--sahha-text-900)]"
    >
      <ArrowLeft size={12} weight="bold" /> {label}
    </button>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder, autoComplete }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string; autoComplete?: string }) {
  return (
    <div>
      <label className="block text-[12.5px] font-semibold text-[var(--sahha-text-700)]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-1.5 h-11 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-[14px] placeholder:text-[var(--sahha-text-400)] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
      />
    </div>
  );
}
